/**
 * Talking to the SharePass browser extension, for credentials that sign the recipient in without
 * showing the password (Yuri, 29/09/2026). The extension listens to this site only (its
 * `externally_connectable`), confirms with the person itself, and fills the site's sign-in form.
 */

/** The Chrome Web Store extension; a build may add others (an unpacked copy to test with). */
const STORE_ID = 'hbejbbjkjojenjopiinjjegokobgdcbo';
export const EXTENSION_IDS = [
    STORE_ID,
    ...(import.meta.env.VITE_EXTENSION_IDS ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
];

export const EXTENSION_STORE_URL = `https://chromewebstore.google.com/detail/${STORE_ID}`;

export type SignInCredentials = {
    url: string;
    username: string;
    password: string;
    totpSeed?: string;
    submit: boolean;
};

type Reply = { ok: boolean; error?: string; features?: string[] } | undefined;

type ChromeRuntime = { sendMessage: (id: string, message: unknown, callback: (reply: Reply) => void) => void };

/** Only present when an installed extension lets this page talk to it. */
const runtime = (): ChromeRuntime | null => {
    const chrome = (globalThis as { chrome?: { runtime?: Partial<ChromeRuntime> } }).chrome;
    return typeof chrome?.runtime?.sendMessage === 'function' ? (chrome.runtime as ChromeRuntime) : null;
};

function send(id: string, message: unknown, timeoutMs: number): Promise<Reply> {
    const api = runtime();
    if (!api) return Promise.resolve(undefined);
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(undefined), timeoutMs);
        try {
            api.sendMessage(id, message, (reply) => {
                clearTimeout(timer);
                // Reading lastError keeps Chrome from logging "Could not establish connection".
                void (globalThis as { chrome?: { runtime?: { lastError?: unknown } } }).chrome?.runtime?.lastError;
                resolve(reply);
            });
        } catch {
            clearTimeout(timer);
            resolve(undefined);
        }
    });
}

/** The id of an installed extension that can sign in, or null. */
export async function findSignInExtension(timeoutMs = 1500): Promise<string | null> {
    for (const id of EXTENSION_IDS) {
        const reply = await send(id, { type: 'hello' }, timeoutMs);
        if (reply?.ok && reply.features?.includes('signIn')) return id;
    }
    return null;
}

/** Hands the credentials over; the extension then asks the person in its own window. */
export async function signInWithExtension(id: string, credentials: SignInCredentials): Promise<string | null> {
    const reply = await send(id, { type: 'signIn', ...credentials }, 10_000);
    if (reply?.ok) return null;
    return reply?.error ?? 'The SharePass extension did not answer. Make sure it is turned on, then try again.';
}

/** The sign-in page a credentials secret carries as its hostname, as an https address. */
export function signInUrlOf(hostname: string | undefined): string | null {
    const raw = (hostname ?? '').trim();
    if (!raw) return null;
    try {
        const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
        return url.protocol === 'https:' ? url.href : null;
    } catch {
        return null;
    }
}
